import { FC } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import styled from 'styled-components'
import { Box, Datepicker, Text } from '@mrshmllw/smores-react'

import Loader from '../../components/Loader'

import { useUpdateInceptionDateMutation } from 'app/services'
import { formatDateStr, identify, useEventTracking } from 'utils'

interface InceptionDateBlockProps {
  initialStartDate: string
}

export const InceptionDateBlock: FC<InceptionDateBlockProps> = ({ initialStartDate }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { quoteId } = useParams()
  const track = useEventTracking()

  if (!quoteId) {
    throw new Error('Route does not have a quote ID')
  }

  const [updateInceptionDate, { isLoading: isSubmitting }] = useUpdateInceptionDateMutation()

  const onDateSelected = async (inceptionDate: string) => {
    const message = 'Select new inception date'
    try {
      await updateInceptionDate({ quoteId, inceptionDate })
      track(message)
      identify(
        quoteId,
        {
          newInceptionDate: formatDateStr(inceptionDate, '1 January 1970'),
        },
        undefined,
      )
      navigate(0)
    } catch {
      navigate(`/quote/${quoteId}/error`, { state: { from: location.pathname } })
    }
  }

  if (isSubmitting) {
    return <Loader showLine />
  }

  return (
    <Wrapper>
      <HeaderWrapper>
        <Text typo="hero-alternate" mb="12px">
          Please update your insurance start date
        </Text>
        <Description tag="p">
          Your previous start date of {initialStartDate} has now lapsed. This may affect your price.
        </Description>
      </HeaderWrapper>
      <DateWrapper>
        {/* Range is 60 days so today + 59 */}
        <Datepicker onDateSelect={onDateSelected} range={59} disableWeekend={false} />
      </DateWrapper>
    </Wrapper>
  )
}

const Wrapper = styled(Box)`
  padding: 40px 0 80px 0;
`

const ContentWrapper = styled(Box)`
  width: 100%;
  max-width: 740px;
  padding: 0 16px;
  margin: 0 auto;
`

const HeaderWrapper = styled(ContentWrapper)`
  padding-bottom: 40px;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`

const DateWrapper = styled(ContentWrapper)`
  display: flex;
  justify-content: center;
  padding-top: 32px;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`

const Description = styled(Text)`
  font-size: 16px;
  line-height: 22px;
`
